import { i18n } from '../../i18n/i18n'

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { connect, ConnectedProps } from 'react-redux'
import { RootState } from '../../store'
import routes from '../../routes/routes'
import palletApi from '../../apis/pallet.api'
import inventoryApi, {
  InventoryReportExportRequest
} from '../../apis/inventory.api'
import alertService from '../../services/alert.service'
import downloadService from '../../services/download.service'
import confirmService from '../../services/confirm.service'
import printService from '../../services/print.service'
import { ConfirmParams } from '../../components/Confirm/Confirm.state'
import {
  replacePage,
  replaceLimit,
  replaceSearch,
  replaceFilter
} from './BarePallets.slice'
import { paginate, deletePallet } from './BarePallets.thunk'
import Card from '../../components/Card'
import ContentLayout from '../../components/ContentLayout'
import ContentMain from '../../components/ContentMain'
import DataTable from '../../components/DataTable'
import { TableHorizontalHeader } from '../../components/TableHorizontal'
import { ActionName } from '../../components/Actions'
import HeadingRow from '../../components/HeadingRow'
import Button from '../../components/Button'
import RangeExport from '../../components/RangeExport'
import { WatermarkHelper } from '../../components/WatermarkHelper'

interface Props extends RouteComponentProps, ConnectedProps<typeof connector> {
}

class BarePallets extends Component<Props> {
  headers: TableHorizontalHeader[] = [
    { heading: i18n('Item'), path: 'item.code' },
    { heading: i18n('Work order'), path: 'workOrderCode' },
    { heading: i18n('Code'), path: 'code' },
    { heading: i18n('Pallet'), path: 'number' },
    {
      heading: i18n('Quantity'),
      path: 'quantity',
      tooltip: ['item.category.name']
    },
    { heading: i18n('Total QPC'), path: 'unitQuantity' },
    { heading: i18n('Location'), path: 'locationName' },
    {
      heading: i18n('Created at'),
      path: 'createdAt',
      date: true,
      tooltip: ['inventory.createdUser.username']
    },
    { heading: i18n('Actions') }
  ]

  constructor (props: Props) {
    super(props)
    this.handleExportClick = this.handleExportClick.bind(this)
    this.handleFilterClick = this.handleFilterClick.bind(this)
    this.handleActionClick = this.handleActionClick.bind(this)
    this.handleLimitChange = this.handleLimitChange.bind(this)
    this.handleSearch = this.handleSearch.bind(this)
    this.handlePaginate = this.handlePaginate.bind(this)
    this.handleCreateClick = this.handleCreateClick.bind(this)
  }

  handleLimitChange (limit: string) {
    this.props.replaceLimit(Number(limit))
  }

  handleSearch (search: string) {
    this.props.replaceSearch(search)
  }

  handlePaginate (page: number) {
    this.props.replacePage(page)
  }

  handleExportClick (startAt: string, endAt: string) {
    const payload: InventoryReportExportRequest = {}
    if (startAt) payload.startAt = startAt
    if (endAt) payload.endAt = endAt
    payload.status = 'bare'
    inventoryApi.exportReport(payload, report => {
      if (!report) {
        alertService.info('No results to export')
        return
      }
      downloadService.inventories(report)
    })
  }

  handleFilterClick (startAt: string, endAt: string) {
    this.props.replaceFilter(startAt, endAt)
  }

  handleCreateClick () {
    this.props.history.push(routes.barePalletCreate)
  }

  handleActionClick (id: number, action: ActionName) {
    if (action === 'print') {
      const confirm: ConfirmParams = {
        id,
        question: i18n('Are you sure to print the label again?'),
        confirmText: i18n('Yes'),
        cancelText: i18n('No'),
        onConfirm: () => this.handlePrintConfirm(id)
      }
      confirmService.push(confirm)
    } else if (action === 'delete') {
      const confirm: ConfirmParams = {
        id,
        type: 'warning',
        question: i18n('This bare pallet will be entirely removed from the system!'),
        confirmText: i18n('Delete'),
        cancelText: i18n('Cancel'),
        onConfirm: () => this.props.deletePallet(id)
      }
      confirmService.push(confirm)
    }
  }

  handlePrintConfirm (palletId: number) {
    palletApi.print(palletId, async markups => {
      if (!markups) return

      markups = await WatermarkHelper.applyWatermarks(markups)
      printService.printMarkups(markups)
    })
  }

  componentDidMount () {
    this.props.paginate()
  }

  render () {
    return (
            <ContentLayout>
                <ContentMain layout="full">
                    <Card>
                        <HeadingRow
                            heading={i18n('Bare Pallets')}
                            doc="pallets-view-all-bare-pallets">
                            <RangeExport
                                onExport={this.handleExportClick}
                                onFilter={this.handleFilterClick}/>
                            <div className='self-end'>
                                <Button
                                    text={i18n('Create')}
                                    onClick={this.handleCreateClick}/>
                            </div>
                        </HeadingRow>
                        <DataTable
                            headers={this.headers}
                            data={this.props.data}
                            total={this.props.total}
                            from={this.props.from}
                            to={this.props.to}
                            currentPage={this.props.currentPage}
                            lastPage={this.props.lastPage}
                            limit={String(this.props.limit)}
                            search={this.props.search}
                            onLimtChange={this.handleLimitChange}
                            onSearch={this.handleSearch}
                            onPaginate={this.handlePaginate}
                            onActionClick={this.handleActionClick}/>
                    </Card>
                </ContentMain>
            </ContentLayout>
    )
  }
}

const mapStateToProps = (state: RootState) => {
  const { result, limit, search } = state.barePallets
  const { data, total, from, to, currentPage, lastPage } = result
  return { data, total, from, to, currentPage, lastPage, limit, search }
}

const mapDispatchToProps = {
  paginate,
  deletePallet,
  replacePage,
  replaceLimit,
  replaceSearch,
  replaceFilter
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(withRouter(BarePallets))
